import './Dialog.scss';
import { useCallback, useEffect, useRef } from 'react';

import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { observer } from 'mobx-react';

import { animations } from '../../../config/animations';
import { useHiddenBodyOverflow } from '../../../hooks/useHiddenBodyOverflow';
import { Loader } from '../../Loader/Loader';
import { Overlay } from '../../Overlay/Overlay';
import { ArrowLeftIcon } from '../../SVGIcons/ArrowLeftIcon';
import type { IDialogProps } from '../types';

const OWN_ANIMATION_STEPS = 2;

export const DialogOldDesign = observer(
  ({
    className,
    classNameBody,
    classNameBackdrop,
    classNameOverlay,
    classNameParent,
    classNameTitle,
    isOpen,
    title,
    subtitle,
    children,
    onClose,
    onArrow,
    loading,
    onBackdropClick,
    onAnimationEnd,
    disableBackdropClick,
    headerReplacement,
    disableLoader,
    dataTestId,
    backBtnDataTestId,
    closeBtnDataTestId,
    onFullyOpened,
  }: IDialogProps) => {
    const handleKeydown = useCallback(
      (event: KeyboardEvent) => {
        if (isOpen && onClose && event.key === 'Escape') {
          onClose();
        }
      },
      [isOpen, onClose],
    );

    const childAnimationsCountRef = useRef<number | null>(null);
    const animationStepsPassed = useRef(0);
    const ready = useRef(false);

    useHiddenBodyOverflow(Boolean(isOpen));

    const handleAnimationPart = () => {
      const childAnimationsCount = childAnimationsCountRef.current;

      if (ready.current || childAnimationsCount == null || typeof onFullyOpened !== 'function') {
        return;
      }

      if (++animationStepsPassed.current === OWN_ANIMATION_STEPS + childAnimationsCount) {
        ready.current = true;

        return onFullyOpened();
      }
    };

    useEffect(() => {
      document.addEventListener('keydown', handleKeydown, false);

      return () => {
        document.removeEventListener('keydown', handleKeydown, false);
      };
    }, [handleKeydown]);

    const contentRef = useCallback((instance: HTMLDivElement | null) => {
      if (instance) {
        childAnimationsCountRef.current = instance.getAnimations({ subtree: true }).length;
      }
    }, []);

    return (
      <AnimatePresence initial={isOpen}>
        <Overlay
          backdropClassName={classNames('dialog-overlay-backdrop', classNameBackdrop)}
          className={classNames('dialog-overlay', classNameOverlay)}
          disableBackdropClick={disableBackdropClick}
          onClick={onBackdropClick}
        >
          <motion.div
            key="modal"
            ref={contentRef}
            animate={animations.scale.visible}
            className={classNames('dialog-wrapper', classNameParent)}
            exit={animations.scale.hidden}
            initial={animations.scale.hidden}
            role="dialog"
            onAnimationComplete={handleAnimationPart}
            onTransitionEnd={handleAnimationPart}
          >
            <div
              className={classNames('dialog', className)}
              data-testid={dataTestId}
              onAnimationEnd={() => {
                if (typeof onAnimationEnd === 'function') {
                  onAnimationEnd();
                }

                handleAnimationPart();
              }}
            >
              {loading && !disableLoader && (
                <div className="dialog__body-loader">
                  <Loader />
                </div>
              )}
              {headerReplacement ? (
                headerReplacement
              ) : (
                <div
                  className={classNames('dialog__header', {
                    'dialog__header-with-title': !!title,
                  })}
                >
                  <div className={classNames('animation-slide-up', 'dialog__header-title', classNameTitle)}>
                    {title}
                  </div>
                  {onArrow && (
                    <button className="dialog__header-btn-arrow" data-testid={backBtnDataTestId} onClick={onArrow}>
                      <ArrowLeftIcon />
                    </button>
                  )}
                  {subtitle && <div className="dialog__header-subtitle">{subtitle}</div>}
                  {onClose && (
                    <button
                      className="dialog__header-btn"
                      data-testid={closeBtnDataTestId || 'closeButton'}
                      onClick={onClose}
                    >
                      <div className="close-btn">&times;</div>
                    </button>
                  )}
                </div>
              )}

              <div className={classNames('dialog__body', classNameBody)}>{children}</div>
            </div>
          </motion.div>
        </Overlay>
      </AnimatePresence>
    );
  },
);
